import { ErrorCard } from '@starsoft/common/components';
import { KYCStatus } from '@starsoft/common/enums';
import {
  useKycMeByOriginIdWithOrigin,
  useLottiePlayer,
  useSearchParams,
  useTranslation,
} from '@starsoft/common/hooks';
import UserPaymentDetailsKycCheckSkeleton from './Skeleton';
import { UserPaymentDetailsKycCheckProps } from './props';
import styles from './styles.module.scss';

/**
 * UserPaymentDetailsKycCheck Component
 * This component is responsible for displaying the KYC (Know Your Customer) status of a user.
 * It shows different messages and animations based on the KYC status.
 *
 * @param {UserPaymentDetailsKycCheckProps} props - The properties for the component.
 * @param {boolean} props.showOnlyDescription - Flag indicating whether to show only the description.
 *
 * @returns {JSX.Element} The rendered component displaying KYC status or an error card if an error occurs.
 *
 * @example
 * // Usage example:
 * <UserPaymentDetailsKycCheck showOnlyDescription={true} />
 */
export default function UserPaymentDetailsKycCheck({
  showOnlyDescription,
}: UserPaymentDetailsKycCheckProps) {
  const { t } = useTranslation('withdraw');
  const search: URLSearchParams = useSearchParams();

  const { error, isPending, refetch, kyc } = useKycMeByOriginIdWithOrigin();

  const { lottieAnimationRef } = useLottiePlayer({
    path: `/${!kyc || (!!kyc && kyc?.status !== KYCStatus.Verified) ? 'kyc-rejected' : 'empty-search'}.json`,
    dependecyArray: [isPending],
  });

  /**
   * getLabel Function
   * Determines the label to display based on the KYC status and search parameters.
   *
   * @returns {string} The label key to be translated.
   */
  function getLabel() {
    if (!kyc) {
      return 'kyc_required_label';
    }

    if (kyc?.status !== KYCStatus.Verified) {
      return 'verify_kyc_label';
    }

    if (!!search.get('status')) {
      return 'kyc_success_withdraw_title';
    }

    return 'empty_payment_account';
  }

  /**
   * getDescription Function
   * Determines the description to display based on the KYC status and search parameters.
   *
   * @returns {string} The description key to be translated.
   */
  function getDescription() {
    if (!kyc || kyc?.status !== KYCStatus.Verified) {
      return 'kyc_required_description';
    }

    if (!!search.get('status')) {
      return 'kyc_success_withdraw_description';
    }

    return 'empty_payment_description';
  }

  if (isPending) {
    return <UserPaymentDetailsKycCheckSkeleton />;
  }

  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  if (showOnlyDescription) {
    return (
      <div className={styles.container}>
        <div className={styles.container__wrapper__description}>
          {t(getDescription())}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.container__lottie} ref={lottieAnimationRef} />
      <div className={styles.container__wrapper}>
        <div className={styles.container__wrapper__label}>{t(getLabel())}</div>
        <div className={styles.container__wrapper__description}>
          {t(getDescription())}
        </div>
      </div>
    </div>
  );
}
